<script module>
    export { default as layout } from '../../layouts/AdminLayout.svelte';
</script>

<script lang="ts">
    import { Link, router } from '@inertiajs/svelte';
    import { slide } from 'svelte/transition';
    import DataTable, { type Paginator } from '../../components/DataTable.svelte';
    import { show } from '@/routes/admin/cases';
    import { cases as casesRoute } from '@/routes/admin';
    import { formatDate, formatDateTime, todayString } from '../../utils/dates';

    interface StatusInfo {
        value: string;
        label: string;
        color: string;
        icon: string;
    }

    const colorClasses: Record<string, { badge: string; filter: string; filterActive: string }> = {
        gray:    { badge: 'bg-gray-800 text-gray-400',       filter: 'bg-gray-800 text-gray-400 hover:bg-gray-700',       filterActive: 'bg-gray-600 text-white ring-2 ring-gray-400 ring-offset-1 ring-offset-gray-950' },
        amber:   { badge: 'bg-amber-900 text-amber-300',     filter: 'bg-amber-900 text-amber-300 hover:bg-amber-800',     filterActive: 'bg-amber-600 text-white ring-2 ring-amber-400 ring-offset-1 ring-offset-gray-950' },
        emerald: { badge: 'bg-emerald-900 text-emerald-300',  filter: 'bg-emerald-900 text-emerald-300 hover:bg-emerald-800', filterActive: 'bg-emerald-600 text-white ring-2 ring-emerald-400 ring-offset-1 ring-offset-gray-950' },
        violet:  { badge: 'bg-violet-900 text-violet-300',   filter: 'bg-violet-900 text-violet-300 hover:bg-violet-800',   filterActive: 'bg-violet-600 text-white ring-2 ring-violet-400 ring-offset-1 ring-offset-gray-950' },
        rose:    { badge: 'bg-rose-900 text-rose-300',       filter: 'bg-rose-900 text-rose-300 hover:bg-rose-800',       filterActive: 'bg-rose-600 text-white ring-2 ring-rose-400 ring-offset-1 ring-offset-gray-950' },
    };

    function statusClasses(info: StatusInfo) {
        return colorClasses[info.color] ?? colorClasses.gray;
    }

    let {
        cases,
        totalCount = null,
        topics = [],
        availableStatuses = [],
        sort = 'id',
        direction = 'desc',
        filters = { topic: [], status: [] },
    }: {
        cases: Paginator;
        totalCount: number | null;
        topics: { id: number; name: string; disabled: boolean }[];
        availableStatuses: StatusInfo[];
        sort: string;
        direction: 'asc' | 'desc';
        filters: { topic: number[]; status: string[] };
    } = $props();

    const statusByValue = $derived(Object.fromEntries(availableStatuses.map(s => [s.value, s])));

    const filterCount = $derived(filters.status.length + filters.topic.length);
    const hasFilters = $derived(filterCount > 0);
    let filtersOpen = $state(false);
    let filterArea: HTMLDivElement;

    function handleClickOutside(e: MouseEvent) {
        if (filtersOpen && filterArea && !filterArea.contains(e.target as Node)) {
            filtersOpen = false;
        }
    }

    function buildFilterUrl(overrides: { status?: string[]; topic?: number[] }) {
        const params = new URLSearchParams();
        if (sort !== 'id') params.set('sort', sort);
        if (direction !== 'desc') params.set('direction', direction);

        const statuses = overrides.status ?? filters.status;
        for (const s of statuses) params.append('status[]', s);

        const topicIds = overrides.topic ?? filters.topic;
        for (const t of topicIds) params.append('topic[]', String(t));

        const qs = params.toString();
        return casesRoute().url + (qs ? `?${qs}` : '');
    }

    function toggleStatus(value: string) {
        const next = filters.status.includes(value)
            ? filters.status.filter(s => s !== value)
            : [...filters.status, value];
        router.visit(buildFilterUrl({ status: next }), { preserveScroll: true, preserveState: true });
    }

    function toggleTopic(id: number) {
        const next = filters.topic.includes(id)
            ? filters.topic.filter(t => t !== id)
            : [...filters.topic, id];
        router.visit(buildFilterUrl({ topic: next }), { preserveScroll: true, preserveState: true });
    }

    function clearFilters() {
        router.visit(buildFilterUrl({ status: [], topic: [] }), { preserveScroll: true, preserveState: true });
    }

    function handleSort(key: string, dir: 'asc' | 'desc') {
        router.visit(casesRoute({ mergeQuery: { sort: key, direction: dir } }), {
            preserveScroll: true,
            preserveState: true,
        });
    }

    const today = todayString();

    const columns = [
        { key: 'id', label: 'ID', shrink: true },
        { key: null, label: 'History', grow: true },
        { key: null, label: 'Submitted By' },
        { key: 'created_at', label: 'Created' },
        { key: null, label: 'Actions' },
    ];
</script>

<svelte:document onclick={handleClickOutside} />

<svelte:head>
    <title>Cases — Admin — Case of the Day</title>
</svelte:head>

<div bind:this={filterArea}>
<div class="flex items-center justify-between mb-4">
    <h1 class="text-2xl font-bold text-gray-100">Cases</h1>
    <div class="flex items-center gap-2">
        {#if hasFilters && !filtersOpen}
            <div class="hidden sm:flex items-center gap-4 flex-wrap">
                {#if filters.status.length > 0}
                    <div class="inline-flex items-center gap-0 rounded-full overflow-hidden ring-2 ring-gray-600 ring-offset-1 ring-offset-gray-950">
                        {#each filters.status as s (s)}
                            {@const info = statusByValue[s]}
                            {#if info}
                                {@const cls = statusClasses(info)}
                                <button
                                    onclick={() => toggleStatus(s)}
                                    class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium hover:opacity-75 transition-opacity {cls.filterActive.replace(/ring[^ ]* /g, '')}"
                                    title="Remove {info.label} filter"
                                >
                                    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                                    {@html info.icon.replace('<svg', '<svg class="size-3"')}
                                    {info.label}
                                    <svg class="size-2.5 opacity-60" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>
                                </button>
                            {/if}
                        {/each}
                    </div>
                {/if}
                {#if filters.topic.length > 0}
                    <div class="inline-flex items-center gap-0 rounded-full overflow-hidden ring-2 ring-cyan-700 ring-offset-1 ring-offset-gray-950">
                        {#each filters.topic as tid (tid)}
                            {@const topic = topics.find(t => t.id === tid)}
                            {#if topic}
                                <button
                                    onclick={() => toggleTopic(tid)}
                                    class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-cyan-600 text-white hover:opacity-75 transition-opacity"
                                    title="Remove {topic.name} filter"
                                >
                                    <svg class="size-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path opacity=".4" d="M32.5 96l0 149.5c0 17 6.7 33.3 18.7 45.3l192 192c25 25 65.5 25 90.5 0L483.2 333.3c25-25 25-65.5 0-90.5l-192-192C279.2 38.7 263 32 246 32L96.5 32c-35.3 0-64 28.7-64 64zm112 16a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>
                                    {topic.name}
                                    <svg class="size-2.5 opacity-60" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>
                                </button>
                            {/if}
                        {/each}
                    </div>
                {/if}
            </div>
        {/if}
        <button
            onclick={() => filtersOpen = !filtersOpen}
            class="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors {filtersOpen ? 'bg-cyan-600 text-white' : hasFilters ? 'bg-cyan-900 text-cyan-300 hover:bg-cyan-800' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}"
        >
            <svg class="size-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M3.9 54.9C10.5 40.9 24.5 32 40 32l432 0c15.5 0 29.5 8.9 36.1 22.9s4.6 30.5-5.2 42.5L320 320.9 320 448c0 12.1-6.8 23.2-17.7 28.6s-23.8 4.3-33.5-3l-64-48c-8.1-6-12.8-15.5-12.8-25.6l0-79.1L9 97.3C-.7 85.4-2.8 68.8 3.9 54.9z"/></svg>
            Filters
            {#if filterCount > 0}
                <span class="inline-flex items-center justify-center size-5 rounded-full bg-white/20 text-[11px] font-bold leading-none">{filterCount}</span>
            {/if}
        </button>
    </div>
</div>

{#if filtersOpen}
    <div class="bg-gray-900 rounded-lg border border-gray-700 p-4 mb-4 space-y-3" transition:slide={{ duration: 200 }}>
        <div class="flex items-center gap-2 flex-wrap">
            <span class="text-xs text-gray-500 uppercase tracking-wide font-medium w-14 shrink-0">Status</span>
            {#each availableStatuses as st (st.value)}
                {@const cls = statusClasses(st)}
                <button
                    onclick={() => toggleStatus(st.value)}
                    class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors {filters.status.includes(st.value) ? cls.filterActive : cls.filter}"
                >
                    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                    {@html st.icon.replace('<svg', '<svg class="size-3"')}
                    {st.label}
                </button>
            {/each}
        </div>

        {#if topics.length > 1}
            <div class="flex items-center gap-2 flex-wrap">
                <span class="text-xs text-gray-500 uppercase tracking-wide font-medium w-14 shrink-0">Topic</span>
                {#each topics as topic (topic.id)}
                    <button
                        onclick={() => toggleTopic(topic.id)}
                        class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors {filters.topic.includes(topic.id) ? 'bg-cyan-600 text-white ring-2 ring-cyan-400 ring-offset-1 ring-offset-gray-900' : topic.disabled ? 'bg-gray-800 text-gray-500 hover:bg-gray-700' : 'bg-cyan-900 text-cyan-300 hover:bg-cyan-800'}"
                    >
                        {topic.name}{#if topic.disabled} <span class="ml-0.5 opacity-60">(off)</span>{/if}
                    </button>
                {/each}
            </div>
        {/if}

        {#if hasFilters}
            <div class="flex justify-end pt-1">
                <button
                    onclick={clearFilters}
                    class="text-xs text-gray-400 hover:text-gray-200"
                >
                    Clear all filters
                </button>
            </div>
        {/if}
    </div>
{/if}
</div>

<DataTable
    {columns}
    items={cases}
    {sort}
    {direction}
    onsort={handleSort}
    onrowclick={(c) => router.visit(show.url(c.id))}
    {totalCount}
    onclearfilters={hasFilters ? clearFilters : undefined}
>
    {#snippet row(c)}
        <td class="pl-6 pr-2 py-4 text-sm text-gray-500 whitespace-nowrap w-px">{c.id}</td>
        <td class="px-6 py-4 text-sm text-gray-400">
            <div class="flex items-start gap-3">
                {#if c.image_url}
                    <img src={c.image_url} alt="" class="size-16 sm:size-24 rounded object-cover shrink-0 bg-gray-800 ring-1 ring-gray-700" />
                {:else}
                    <div class="size-16 sm:size-24 rounded bg-gray-800 shrink-0 ring-1 ring-gray-700 flex items-center justify-center text-gray-600">
                        <svg class="size-7 sm:size-10" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor"><path opacity=".4" d="M0 96L0 416c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32C28.7 32 0 60.7 0 96zM66.7 403.1c-4.1-7.9-3.5-17.5 1.6-24.8l56-80c4.5-6.4 11.8-10.2 19.7-10.2s15.2 3.8 19.7 10.2l26.4 37.8 61.4-100.5c4.4-7.1 12.1-11.5 20.5-11.5s16.1 4.4 20.5 11.5l88 144c4.5 7.4 4.7 16.7 .5 24.3S368.7 416 360 416L88 416c-8.9 0-17.2-5-21.3-12.9zM176 160a48 48 0 1 1 -96 0 48 48 0 1 1 96 0z"/><path fill="currentColor" d="M292.5 235.5c-4.4-7.1-12.1-11.5-20.5-11.5s-16.1 4.4-20.5 11.5L190.1 336 163.7 298.2c-4.5-6.4-11.8-10.2-19.7-10.2s-15.2 3.8-19.7 10.2l-56 80c-5.1 7.3-5.8 16.9-1.6 24.8S79.1 416 88 416l272 0c8.7 0 16.7-4.7 20.9-12.3s4.1-16.8-.5-24.3l-88-144z"/></svg>
                    </div>
                {/if}
                <div class="min-w-0">
                    <span class="line-clamp-3">{c.history_excerpt}</span>
                    <div class="flex items-center gap-1.5 mt-1.5">
                        <span class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap {statusClasses(c.status_info).badge}">
                            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                            {@html c.status_info.icon.replace('<svg', '<svg class="size-3"')}
                            {c.status_info.label}
                        </span>
                        {#if c.topic}
                            <span class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-cyan-900 text-cyan-300 whitespace-nowrap">
                                <svg class="size-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path opacity=".4" d="M32.5 96l0 149.5c0 17 6.7 33.3 18.7 45.3l192 192c25 25 65.5 25 90.5 0L483.2 333.3c25-25 25-65.5 0-90.5l-192-192C279.2 38.7 263 32 246 32L96.5 32c-35.3 0-64 28.7-64 64zm112 16a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>
                                {c.topic.name}
                            </span>
                        {/if}
                        {#if c.scheduled_dates?.length}
                            <span class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-violet-900 text-violet-300 whitespace-nowrap">
                                <svg class="size-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor"><path opacity=".4" d="M96 32l0 32L0 64 0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-384-96 0 0-32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 32L160 64l0-32c0-17.7-14.3-32-32-32S96 14.3 96 32zM152 192l144 0c13.3 0 24 10.7 24 24l0 112c0 13.3-10.7 24-24 24l-144 0c-13.3 0-24-10.7-24-24l0-112c0-13.3 10.7-24 24-24z"/><path fill="currentColor" d="M128 216l0 112c0 13.3 10.7 24 24 24l144 0c13.3 0 24-10.7 24-24l0-112c0-13.3-10.7-24-24-24l-144 0c-13.3 0-24 10.7-24 24z"/></svg>
                                {#each c.scheduled_dates as d, i (d)}{#if i > 0}, {/if}<span class={d === today ? 'font-bold text-violet-100' : d < today ? 'text-violet-500' : ''}>{formatDate(d, { month: 'short', day: 'numeric' })}</span>{/each}
                            </span>
                        {/if}
                    </div>
                </div>
            </div>
        </td>
        <td class="px-6 py-4 text-sm text-gray-400">{c.submitted_by?.name ?? '—'}</td>
        <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(c.created_at)}</td>
        <td class="px-6 py-4 text-sm">
            <Link href={show(c.id)} class="text-cyan-400 hover:underline">View</Link>
        </td>
    {/snippet}

</DataTable>
